<template>
  <div
    v-if="showProductsList"
    id="products-list-div"
    class="push-me-downwards border pt-1 px-1"
  >

    <b-container class="px-0">
      <b-row>
        <b-col cols="7" class="right">
          <b-button
            v-if="canCreateProducts"
            v-show="showProductSearch"
            variant="primary"
            size="sm"
            class="mb-1"
            @click="openUnitModal()"
          >
            <span>Create new product</span>
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-container v-if="products.length > 0">
      <b-row class="list-products-header-row">
        <b-col cols="1" class="p-0 mx-0">&nbsp;</b-col>
        <b-col cols="4" class="p-0 mx-0 text-center">
          <b class="text-white my-1">Product</b>
        </b-col>
        <b-col cols="2" class="p-0 mx-0 text-center">
          <b class="text-white my-1">Price</b>
        </b-col>
        <b-col cols="3" class="p-0 mx-0 text-center">
          <b class="text-white my-1 text-center">Quantity</b>
        </b-col>
        <b-col cols="2" class="p-0 mx-0 text-center">
          <b class="text-white my-1">Total</b>
        </b-col>
      </b-row>

      <b-row
        v-for="product in products"
        :key="product.id"
        class="py-1 my-1 selected-products-row"
      >
        <template v-if="product.items_quantity > 0">
          <b-col cols="1" class="p-0 mx-0">
            <img
              :src="productPictureSrc(product)"
              :style="{ width: '100%', border: '1px solid #DDDDDD' }"
              class="small-horizontal-padding"
            >
          </b-col>
          <b-col cols="4" class="p-0 mx-0">
            <span class="mt-2" :class="{ 'deleted-product-name': product.deleted }">{{ product.name }} <br/> <small><i>{{ product.sku }}</i></small></span>
          </b-col>
          <b-col cols="2" class="p-0 mx-0">
            <span class="mt-2">{{ $formatCurrency(product.price, null) }}</span>
          </b-col>
          
          <b-col cols="3" class="text-center p-0 mx-0">
            <vue-number-input
              inline
              controls
              :readonly="isPlatformSale || productsReadOnly || product.deleted"
              :model-value="product.items_quantity"
              :min="0"
              :max="maxProductQuantity(product)"
              @update:modelValue="updateProductQuantity($event, product.id, product)"
            />
          </b-col>
          <b-col cols="2" class="p-0 mx-0 text-center">
            <span class="mt-2">{{ $formatCurrency(product.items_quantity * product.price) }}</span>
          </b-col>
        </template>
      </b-row>
    </b-container>

    <b-container class="px-0">
      <b-row
        v-show="showProductSearch"
        class="py-2"
      >
        <b-col aria-colspan="6">
          <div class="d-flex align-items-center products-search-row">
            <BarcodeTargetField
              wrapper-class="products-search-barcode flex-grow-1"
              hint-key="order-products-search"
            >
              <b-form-input
                ref="searchInputRef"
                v-model="search_keyword"
                placeholder="Search product by name, SKU or barcode"
                class="barcode-target products-search-input"
              />
            </BarcodeTargetField>
            <b-button
              type="button"
              variant="outline-secondary"
              size="sm"
              class="ml-1 flex-shrink-0"
              @click="clearSearch"
            >
              Clean
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row
        v-if="searchUnits.length > 0"
        class="search-products-header-row border-radius-bottom"
      >
        <b-col cols="1">&nbsp;</b-col>
        <b-col cols="4">
          <span class="my-1">Product</span>
        </b-col>
        <b-col cols="5">
          <span class="my-1">Price</span>
        </b-col>
        <b-col cols="2">
          <span class="my-1 text-center">&nbsp;</span>
        </b-col>
      </b-row>
      <div
        v-show="searchUnits.length > 0"
        class="scrollable-container"
      >
        <b-row
          v-for="(product, index) in searchUnits"
          :key="product.id"
          class="pb-1 m-1"
          :class="{ 'border-bottom': index < searchUnits.length - 1 }"
        >
          <b-col cols="1" class="p-0">
            <img
              :src="productPictureSrc(product)"
              :style="{ width: '100%', border: '1px solid #DDDDDD' }"
            >
          </b-col>
          <b-col cols="4">
            <span class="mt-2">{{ product.name }} <br/> <small><i>{{ product.sku }}</i></small></span>
          </b-col>
          <b-col cols="5">
            <span class="mt-2">{{ $formatCurrency(product.price, null) }}</span>
          </b-col>
          <b-col cols="2" class="text-right p-0">
            <b-button
              type="button"
              variant="dark"
              size="sm"
              @click="addProductFromSearch(product)"
            >
              Add
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-container>

  </div>
  <ManageUnit
    :click-date="clickDate"
    :unit="null"
    :hide-stock-management="true"
    @unit-created="addCreatedProductToOrder"
  />
</template>

<script>
import { watch, ref, computed, provide, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BCol,
  BFormInput,
  BContainer,
  BRow,
} from 'bootstrap-vue'
import { sweetError } from '@/libs/sweet-alerts'
import VueNumberInput from '@chenfengyuan/vue-number-input'
import ManageUnit from '@/views/Components/ManageUnit.vue'
import { ORDER_PRODUCT_ADD_KEY } from '@/constants/injectionKeys'
import { canStaffCreate } from '@/libs/staffPermissions'
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  name: 'Products',
  components: {
    ManageUnit,
    BarcodeTargetField,
    BButton,
    BCol,
    BFormInput,
    BContainer,
    BRow,
    VueNumberInput,
  },
  props: {
    products: {
      type: Array,
      required: true,
    },
    sale: {
      type: Object,
      default: null,
    },
    productsReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:products'],
  setup(props, { emit }) {
    const store = useStore()
    const search_keyword = ref('')
    const searchUnits = ref([])
    const clickDate = ref(null)
    const searchInputRef = ref(null)
    const pendingBarcodeAutoAdd = ref(false)
    let searchInputEl = null

    const isPlatformSale = computed(() => Boolean(props.sale?.platform))
    const productsReadOnly = computed(() => Boolean(props.productsReadOnly))
    const showProductSearch = computed(() => {
      if (productsReadOnly.value) {
        return false
      }
      return !props.sale || !props.sale.platform
    })
    const hasActiveProducts = computed(() => (
      (props.products || []).some((product) => Number(product.items_quantity) > 0)
    ))
    const showProductsList = computed(() => hasActiveProducts.value || showProductSearch.value)
    const canCreateProducts = computed(() => canStaffCreate(store.getters['auth/user'], 'products'))

    const productIdEquals = (a, b) => String(a) === String(b)

    const normalizePicture = (picture) => {
      if (Array.isArray(picture) && picture[0]?.src) {
        return picture
      }
      if (typeof picture === 'string' && picture.trim() !== '') {
        try {
          const parsed = JSON.parse(picture)
          if (Array.isArray(parsed) && parsed[0]?.src) {
            return parsed
          }
        } catch (e) {
          // not JSON
        }
      }
      return [{ name: '', src: NO_IMAGE }]
    }

    const productPictureSrc = (product) => {
      const picture = normalizePicture(product?.picture)
      return picture[0]?.src || NO_IMAGE
    }

    const isStockLimited = (product) => {
      return Boolean(product?.stock_management) && !product?.allow_backorder
    }

    const availableStock = (product) => {
      return Number(product?.available_stock ?? product?.total_stock ?? 0)
    }

    const maxProductQuantity = (product) => {
      if (!isStockLimited(product)) {
        return undefined
      }
      return availableStock(product)
    }

    const canSetQuantity = (productRef, newQuantity) => {
      if (!productRef || newQuantity <= 0 || !isStockLimited(productRef)) {
        return true
      }

      const available = availableStock(productRef)
      if (newQuantity > available) {
        sweetError(
          'Not enough stock',
          `Only ${available} item(s) available in stock.`,
        )
        return false
      }

      return true
    }

    const buildCartProduct = (productId, newQuantity, source) => {
      const data = source?.data ?? source

      return {
        id: productId,
        items_quantity: newQuantity,
        name: data.name,
        sku: data.sku,
        price: data.price,
        picture: normalizePicture(data.picture),
        stock_management: Boolean(data.stock_management),
        allow_backorder: Boolean(data.allow_backorder),
        available_stock: availableStock(data),
        deleted: Boolean(data.deleted),
      }
    }

    const setProducts = (nextProducts) => {
      emit('update:products', nextProducts)
    }

    const removeFromSearchResults = (productId) => {
      searchUnits.value = searchUnits.value.filter((p) => !productIdEquals(p.id, productId))
    }

    const addProductFromSearch = (searchProduct) => {
      if (productsReadOnly.value || !searchProduct?.id) {
        return
      }

      const existing = props.products.find((p) => productIdEquals(p.id, searchProduct.id))
      const currentQty = existing ? parseInt(existing.items_quantity, 10) : 0
      const nextQty = Number.isNaN(currentQty) ? 1 : currentQty + 1

      if (!canSetQuantity(searchProduct, nextQty)) {
        return
      }

      if (existing) {
        setProducts(
          props.products.map((p) => (
            productIdEquals(p.id, searchProduct.id)
              ? { ...p, items_quantity: nextQty }
              : p
          )),
        )
        removeFromSearchResults(searchProduct.id)
        return
      }

      setProducts([
        ...props.products,
        buildCartProduct(searchProduct.id, nextQty, searchProduct),
      ])
      removeFromSearchResults(searchProduct.id)
    }

    const autoAddFromBarcodeScan = () => {
      const units = [...searchUnits.value]
      units.forEach((product) => {
        addProductFromSearch(product)
      })
    }

    const clearSearch = () => {
      pendingBarcodeAutoAdd.value = false
      search_keyword.value = ''
      searchUnits.value = []
    }

    const onBarcodeScanned = () => {
      pendingBarcodeAutoAdd.value = true
    }

    const bindSearchInputBarcodeListener = () => {
      const root = searchInputRef.value?.$el
      const input = root?.matches?.('input') ? root : root?.querySelector?.('input')
      if (!input || input === searchInputEl) {
        return
      }
      if (searchInputEl) {
        searchInputEl.removeEventListener('barcode-scanned', onBarcodeScanned)
      }
      searchInputEl = input
      searchInputEl.addEventListener('barcode-scanned', onBarcodeScanned)
    }

    const updateProductQuantity = (newQuantity, productId, sourceProduct = null) => {
      if (productsReadOnly.value) {
        return false
      }

      const parsedQuantity = parseInt(newQuantity, 10)

      if (Number.isNaN(parsedQuantity)) {
        return false
      }

      const cartProduct = props.products.find((p) => productIdEquals(p.id, productId))
      const searchProduct = searchUnits.value.find((p) => productIdEquals(p.id, productId))
      const productRef = cartProduct || sourceProduct || searchProduct

      if (parsedQuantity > 0 && !canSetQuantity(productRef, parsedQuantity)) {
        return false
      }

      if (cartProduct) {
        if (parsedQuantity === 0) {
          setProducts(props.products.filter((p) => !productIdEquals(p.id, productId)))
        } else {
          setProducts(
            props.products.map((p) => (
              productIdEquals(p.id, productId)
                ? { ...p, items_quantity: parsedQuantity }
                : p
            )),
          )
        }
        return true
      }

      if (parsedQuantity > 0) {
        const fromSearch = sourceProduct || searchProduct
        if (fromSearch) {
          setProducts([
            ...props.products,
            buildCartProduct(productId, parsedQuantity, fromSearch),
          ])
          return true
        }

        store.dispatch('units/loadUnit', productId)
          .then((response) => {
            const unitData = response?.data ?? response
            if (!canSetQuantity(unitData, parsedQuantity)) {
              return
            }
            setProducts([
              ...props.products,
              buildCartProduct(productId, parsedQuantity, unitData),
            ])
          })
          .catch(() => {})
      }

      return false
    }

    const addCreatedProductToOrder = (createdUnit) => {
      const unit = createdUnit?.data ?? createdUnit

      if (!unit?.id || productsReadOnly.value) {
        return
      }

      searchUnits.value = [
        unit,
        ...searchUnits.value.filter((p) => !productIdEquals(p.id, unit.id)),
      ]
      search_keyword.value = unit.name || unit.sku || String(unit.id)

      if (!canSetQuantity(unit, 1)) {
        return
      }

      const cartProduct = buildCartProduct(unit.id, 1, unit)
      const existing = props.products.find((p) => productIdEquals(p.id, unit.id))

      if (existing) {
        setProducts(
          props.products.map((p) => (
            productIdEquals(p.id, unit.id)
              ? { ...cartProduct, items_quantity: 1 }
              : p
          )),
        )
        return
      }

      setProducts([...props.products, cartProduct])
    }

    provide(ORDER_PRODUCT_ADD_KEY, addCreatedProductToOrder)

    watch(
      () => store.state.units.orderCreatedProduct,
      (product) => {
        if (!product?.id) {
          return
        }

        addCreatedProductToOrder(product)
        store.commit('units/SET_ORDER_CREATED_PRODUCT', null)
      },
    )

    const openUnitModal = () => {
      clickDate.value = new Date()
    }

    const parseSearchUnits = (response) => {
      if (Array.isArray(response)) {
        return response
      }
      if (Array.isArray(response?.data)) {
        return response.data
      }
      return []
    }

    watch(search_keyword, (keyword) => {
      if (productsReadOnly.value) {
        searchUnits.value = []
        return
      }

      const trimmed = String(keyword || '').trim()
      if (trimmed.length < 1) {
        searchUnits.value = []
        pendingBarcodeAutoAdd.value = false
        return
      }

      const autoAddOnComplete = pendingBarcodeAutoAdd.value

      store.dispatch('units/searchUnit', {
        keyword: trimmed,
        platformId: Number(props.sale?.platform_id) || 0,
      })
        .then((response) => {
          if (String(search_keyword.value || '').trim() !== trimmed) {
            return
          }

          searchUnits.value = parseSearchUnits(response)

          if (autoAddOnComplete) {
            pendingBarcodeAutoAdd.value = false
            if (searchUnits.value.length > 0) {
              autoAddFromBarcodeScan()
            }
            search_keyword.value = ''
            searchUnits.value = []
          }
        })
        .catch(() => {
          if (String(search_keyword.value || '').trim() === trimmed) {
            searchUnits.value = []
            if (autoAddOnComplete) {
              pendingBarcodeAutoAdd.value = false
            }
          }
        })
    })

    onMounted(() => {
      nextTick(bindSearchInputBarcodeListener)
    })

    onUnmounted(() => {
      if (searchInputEl) {
        searchInputEl.removeEventListener('barcode-scanned', onBarcodeScanned)
        searchInputEl = null
      }
    })

    return {
      search_keyword,
      searchUnits,
      clickDate,
      searchInputRef,
      isPlatformSale,
      productsReadOnly,
      showProductSearch,
      showProductsList,
      canCreateProducts,
      productPictureSrc,
      maxProductQuantity,
      addProductFromSearch,
      addCreatedProductToOrder,
      updateProductQuantity,
      openUnitModal,
      clearSearch,
    }
  },
}
</script>

<style scoped>
.products-search-row {
  width: 100%;
}

.products-search-input {
  flex: 1 1 auto;
  min-width: 0;
}

.deleted-product-name {
  text-decoration: line-through;
  color: #6e6b7b;
}

@media (max-width: 767.98px) {
  #products-list-div {
    font-size: 0.75rem;
  }

  #products-list-div :deep(h5) {
    font-size: 0.8rem;
    margin-top: 0.35rem !important;
    margin-bottom: 0.35rem !important;
  }

  #products-list-div :deep(.form-control),
  #products-list-div :deep(.btn),
  #products-list-div :deep(small) {
    font-size: 0.7rem;
  }

  #products-list-div :deep(.vue-number-input__input) {
    font-size: 0.75rem;
    min-width: 2.25rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  #products-list-div :deep(.vue-number-input--inline > .vue-number-input__input) {
    width: 8.5rem;
  }

  #products-list-div :deep(.vue-number-input--controls > .vue-number-input__button) {
    width: 1.75rem;
  }

  #products-list-div :deep(.vue-number-input--controls > .vue-number-input__input) {
    padding-left: 2.1rem;
    padding-right: 2.1rem;
  }
}
</style>
